import React, { useState } from "react";
import { motion } from "framer-motion";
import { Award, Sparkles, Handshake, CheckCircle2, Star } from "lucide-react";

function WhyChooseUs() {
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      icon: Award,
      title: "Personel Berlisensi & Profesional",
      desc: "Soney Raya di Jayapura melatih dan mencetak tenaga keamanan profesional, disiplin, dan berintegritas tinggi melalui program bersertifikasi dan berstandar nasional.",
      color: "from-green-500 to-teal-500",
      stat: "100+",
      statLabel: "Personel",
    },
    {
      icon: Sparkles,
      title: "Teknologi Keamanan Terkini",
      desc: "Soney Raya melindungi data peserta dengan sistem keamanan digital canggih, enkripsi end-to-end, dan autentikasi ganda untuk menjaga kerahasiaan serta mencegah kebocoran informasi.",
      color: "from-green-500 to-teal-500",
      stat: "4+",
      statLabel: "Sistem",
    },
    {
      icon: Handshake,
      title: "Fokus pada Kemitraan Klien",
      desc: "Soney Raya berkomitmen membangun kemitraan profesional dengan solusi keamanan yang disesuaikan, komunikasi terbuka, dan pelayanan terbaik demi terciptanya lingkungan kerja yang aman dan terpercaya.",
      color: "from-lime-500 to-green-500",
      stat: "200+",
      statLabel: "Klien Terlayani",
    },
  ];

  // Animasi global fade-up
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" },
    }),
  };

  return (
    <section className="relative bg-gradient-to-br from-white via-white/95 to-white py-32 px-6 md:px-16 overflow-hidden">

      {/* 🌿 Background Animated Glow */}
      <motion.div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/3 left-1/3 w-96 h-96 bg-green-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.07, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-teal-400/10 rounded-full filter blur-[120px]"
        ></motion.div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.08)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.08)_2px,transparent_2px)] bg-[size:100px_100px]" />
      </motion.div>

      {/* ✴️ Header */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative z-10 text-center mb-20 max-w-4xl mx-auto"
      >
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-100/10 border border-green-200/30 rounded-full backdrop-blur-sm mb-6"
        >
          <Star className="w-4 h-4 text-green-500 fill-green-500" />
          <span className="text-green-500 text-sm font-mono uppercase tracking-wider">
            Kenapa Memilih Kami
          </span>
        </motion.div>

        <motion.h2
          whileHover={{ scale: 1.02 }}
          className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-tight"
        >
          <span className="text-gray-900">Keunggulan </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-teal-400 to-green-600">
            Kami
          </span>
        </motion.h2>

        <motion.div
          animate={{ scaleX: [0.9, 1.1, 0.9] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mx-auto mb-8"
        ></motion.div>

        <p className="text-lg text-gray-600 leading-relaxed font-light">
          Pilihan terpercaya untuk keamanan properti, aset, dan lingkungan yang
          profesional dan terjamin.
        </p>
      </motion.div>

      {/* 🌟 Features Grid */}
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isHovered = hoveredFeature === index;

            return (
              <motion.div
                key={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                custom={index * 0.3}
                viewport={{ once: true }}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200, damping: 18 }}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                className="group relative"
              >
                {/* ✨ Glow Effect */}
                <motion.div
                  animate={{ opacity: [0, 0.3, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className={`absolute -inset-1 bg-gradient-to-r ${feature.color} rounded-3xl blur-xl opacity-0 group-hover:opacity-40 transition duration-500`}
                ></motion.div>

                {/* 📦 Card */}
                <div className="relative h-full rounded-3xl overflow-hidden border border-gray-200 bg-white/90 backdrop-blur-sm transition-all duration-500 group-hover:shadow-xl group-hover:scale-[1.02]">
                  {/* 🧩 Content */}
                  <div className="relative p-6 space-y-4">
                    <motion.div
                      animate={{
                        rotate: isHovered ? [0, 4, -4, 0] : 0,
                        scale: isHovered ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}
                    >
                      <Icon className="w-8 h-8 text-white" />
                    </motion.div>

                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-green-500 group-hover:to-teal-500 transition-all duration-300">
                      {feature.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed min-h-[80px]">
                      {feature.desc}
                    </p>

                    <div className="h-px bg-gradient-to-r from-transparent via-gray-300/20 to-transparent"></div>

                    {/* 📊 Stats */}
                    <div className="flex items-center justify-between">
                      <motion.div
                        animate={{
                          opacity: isHovered ? [1, 0.8, 1] : 1,
                          scale: isHovered ? [1, 1.05, 1] : 1,
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <p
                          className={`text-2xl font-bold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}
                        >
                          {feature.stat}
                        </p>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">
                          {feature.statLabel}
                        </p>
                      </motion.div>

                      <motion.div
                        animate={{
                          rotate: isHovered ? [0, 360] : 0,
                        }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        className={`w-10 h-10 rounded-full bg-white/10 border border-gray-200 flex items-center justify-center transition-all duration-300 ${isHovered ? "bg-gradient-to-r " + feature.color : ""}`}
                      >
                        <CheckCircle2
                          className={`w-5 h-5 transition-colors duration-300 ${isHovered ? "text-white" : "text-gray-400"}`}
                        />
                      </motion.div>
                    </div>
                  </div>

                  {/* 🔹 Underline Gradient */}
                  <motion.div
                    animate={{ scaleX: isHovered ? 1 : 0 }}
                    transition={{ duration: 0.5 }}
                    className={`absolute bottom-0 left-0 right-0 h-1 origin-left bg-gradient-to-r ${feature.color}`}
                  ></motion.div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default WhyChooseUs;
